import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchReviewListStart, fetchReviewListSuccess, fetchReviewListFailure,
  submitFeedbackStart, submitFeedbackSuccess, submitFeedbackFailure,
} from "../../../../Redux/slices/jcSlice";
import { listForNsmReviewApi } from "../../../../services/jcReview/uploadJcApi";
import { getAllSH5Emails } from "../../../../services/DRM/getSH5Email";
import { submitFeedbackApi } from "../../../../services/jcReview/feedbackApi";
import useNotification from "@shared/hooks/useNotification";
import { CustomButton } from "@shared/components";
import {
  Card, CardContent, Typography, Box, Chip, Button, LinearProgress,
  Dialog, DialogContent, DialogActions, IconButton, Link,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import Loader from "@features/shared/components/Loader";
import Message from "@features/shared/components/Message";
import RatingInput from "../shared/RatingInput";
import MonthYearSelector from "../shared/MonthYearSelector";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const CARD_PALETTE = [
  { border: "#4f46e5", bg: "#eef2ff", divider: "#e0e7ff", chip: "#eef2ff", chipText: "#4f46e5", iconBg: "#e0e7ff", iconColor: "#4f46e5", btnBg: "#4f46e5", btnHover: "#4338ca" },
  { border: "#16a34a", bg: "#f0fdf4", divider: "#dcfce7", chip: "#dcfce7", chipText: "#15803d", iconBg: "#dcfce7", iconColor: "#16a34a", btnBg: "#16a34a", btnHover: "#15803d" },
  { border: "#7c3aed", bg: "#f5f3ff", divider: "#ede9fe", chip: "#ede9fe", chipText: "#6d28d9", iconBg: "#ede9fe", iconColor: "#7c3aed", btnBg: "#7c3aed", btnHover: "#6d28d9" },
];

const ratingColor = (r) => r <= 4 ? "#e60000" : r <= 6 ? "#FF9500" : "#34C759";
const statusLabel = (r) => r <= 4 ? "Needs Improvement" : r <= 6 ? "Satisfactory" : "Good";

const ScoreBar = ({ rating }) => {
  const color = ratingColor(rating);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
      <Typography sx={{ fontSize: "2rem", fontWeight: 700, color, lineHeight: 1 }}>{rating}</Typography>
      <Typography sx={{ fontSize: "1rem", color: "#aaa", lineHeight: 1 }}>/ 10</Typography>
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={rating * 10}
          sx={{
            height: 8, borderRadius: 4, backgroundColor: "#eee",
            "& .MuiLinearProgress-bar": { backgroundColor: color, borderRadius: 4 },
          }}
        />
      </Box>
    </Box>
  );
};

const NSMReviewList = ({ reviewerCode, reviewerName, userEmail }) => {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useNotification();
  const { reviewList, reviewListLoading, submitFeedbackLoading } = useSelector((s) => s.jc);
  const sfToken = useSelector((s) => s?.tokens?.accessToken || "");
  const isMobile = useMediaQuery("(max-width:600px)");

  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [feedbackModal, setFeedbackModal] = useState(null);
  const [rating, setRating] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [ratingError, setRatingError] = useState(null);

  // ZM emails derived from Pidilite_Hierarchy__c via NSM's own email
  const [zmEmails, setZmEmails] = useState("");
  const [hierarchyLoading, setHierarchyLoading] = useState(false);

  // Step 1: Fetch ZM emails from Salesforce hierarchy using NSM's email
  useEffect(() => {
    if (!userEmail) return;
    setHierarchyLoading(true);
    getAllSH5Emails({ userEmailid: userEmail, userRole: "SH7" })
      .then((res) => {
        if (res?.success && res?.data?.records?.length > 0) {
          const emails = [
            ...new Set(res.data.records.map((r) => r.SH5_Email__c).filter(Boolean))
          ].join(",");
          setZmEmails(emails);
        } else {
          setZmEmails("");
        }
      })
      .catch(() => setZmEmails(""))
      .finally(() => setHierarchyLoading(false));
  }, [userEmail]);

  // Step 2: Fetch ZM submissions once ZM emails are available (or month/year changes)
  useEffect(() => {
    if (!zmEmails) return;
    dispatch(fetchReviewListStart());
    listForNsmReviewApi(zmEmails, month, year)
      .then((res) => dispatch(fetchReviewListSuccess(res.data)))
      .catch((err) => dispatch(fetchReviewListFailure(err?.response?.data?.msg || "Failed to load submissions")));
  }, [dispatch, zmEmails, month, year]);

  const openFeedback = (submission) => {
    setFeedbackModal({ submission });
    setRating(submission.rating || null);
    setRemarks(submission.remarks || "");
    setRatingError(null);
  };

  const closeFeedback = () => {
    setFeedbackModal(null);
    setRating(null);
    setRemarks("");
    setRatingError(null);
  };

  const handleSubmitFeedback = async () => {
    if (!rating) { setRatingError("Please select a rating"); return; }
    if (rating < 7 && (!remarks || remarks.trim() === "")) {
      setRatingError("Remarks are required when rating is less than 7"); return;
    }
    setRatingError(null);
    dispatch(submitFeedbackStart());
    try {
      const res = await submitFeedbackApi({
        submissionId: feedbackModal.submission.id,
        reviewerCode, reviewerName, reviewerRole: "SH7",
        rating, remarks: remarks.trim(),
        sfToken,
      });
      dispatch(submitFeedbackSuccess(res.data));
      showSuccess("Feedback submitted successfully");
      closeFeedback();
    } catch (err) {
      const msg = err?.response?.data?.msg || "Failed to submit feedback";
      dispatch(submitFeedbackFailure(msg));
      showError(msg);
    }
  };

  const byZone = reviewList.reduce((acc, sub) => {
    const key = sub.cluster_code || "Unassigned";
    if (!acc[key]) acc[key] = [];
    acc[key].push(sub);
    return acc;
  }, {});

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ px: 0.5 }}>
        <MonthYearSelector month={month} year={year} onMonthChange={setMonth} onYearChange={setYear} />
      </Box>

      {(hierarchyLoading || reviewListLoading) ? (
        <Loader loading />
      ) : !zmEmails ? (
        <Message message="Unable to load team hierarchy. Please refresh the page." />
      ) : reviewList.length === 0 ? (
        <Message message={`No ZM submissions found for ${MONTH_SHORT[month - 1]} ${year}.`} />
      ) : (
        Object.entries(byZone).map(([zmCode, items]) => (
          <Box key={zmCode}>
            <Box sx={{ px: 0.5, py: 0.75, mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontWeight: 700, color: "#3901ff", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: 0.5 }}>
                ZM Code: {zmCode}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#888" }}>
                · {items.length} submission{items.length !== 1 ? "s" : ""}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {items.map((sub, idx) => {
                const hasFeedback = sub.rating !== null && sub.rating !== undefined;
                const submittedDate = new Date(sub.submission_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
                const pal = CARD_PALETTE[idx % CARD_PALETTE.length];

                return (
                  <Card key={sub.id} elevation={0} sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", overflow: "hidden", borderLeft: `4px solid ${pal.border}` }}>
                    <CardContent sx={{ p: "20px !important" }}>
                      {/* Header */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, pb: 2, borderBottom: `1px solid ${pal.divider}`, backgroundColor: pal.bg, borderRadius: 1.5, px: 1.5, pt: 1.5, mx: -0.5 }}>
                        <Box>
                          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#1a1a1a" }}>{sub.user_name}</Typography>
                          <Typography sx={{ fontSize: "0.78rem", color: "#888", mt: 0.25 }}>
                            {MONTH_NAMES[sub.month - 1]} {sub.year}
                          </Typography>
                        </Box>
                        <Chip
                          label={`Version ${sub.version}`}
                          size="small"
                          sx={{ backgroundColor: pal.chip, color: pal.chipText, fontWeight: 600, fontSize: "0.75rem", borderRadius: 2 }}
                        />
                      </Box>

                      {/* File row */}
                      <Box
                        sx={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          backgroundColor: "#f7f7f7", borderRadius: 2, px: 2, py: 1.5, mb: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                          <Box sx={{ width: 36, height: 36, borderRadius: 1.5, backgroundColor: pal.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <DescriptionOutlinedIcon sx={{ color: pal.iconColor, fontSize: 20 }} />
                          </Box>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={sub.original_file_name}>
                            {sub.original_file_name}
                          </Typography>
                        </Box>
                        {sub.file_path && (
                          <Button
                            component="a" href={sub.file_path} target="_blank" rel="noopener noreferrer"
                            variant="contained" size="small" startIcon={<FileDownloadOutlinedIcon />}
                            sx={{
                              ml: 1.5, flexShrink: 0, borderRadius: 2, backgroundColor: pal.btnBg,
                              textTransform: "none", fontWeight: 600, fontSize: "0.82rem", px: 1.5, py: 0.6,
                              "&:hover": { backgroundColor: pal.btnHover },
                            }}
                          >
                            Download
                          </Button>
                        )}
                      </Box>

                      {/* Score / pending */}
                      {hasFeedback ? (
                        <Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                            <Typography sx={{ fontSize: "0.9rem", color: "#666", fontWeight: 500 }}>Score</Typography>
                            <Chip
                              icon={<StarOutlineIcon sx={{ fontSize: "14px !important" }} />}
                              label={statusLabel(sub.rating)}
                              size="small"
                              sx={{
                                backgroundColor: "#FFF3E0", color: ratingColor(sub.rating),
                                fontWeight: 700, fontSize: "0.78rem", borderRadius: 3,
                                "& .MuiChip-icon": { color: ratingColor(sub.rating) },
                              }}
                            />
                          </Box>
                          <ScoreBar rating={sub.rating} />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            border: "1.5px solid #FFD58C", borderRadius: 2, px: 2, py: 1.5,
                            backgroundColor: "#FFFBF0",
                          }}
                        >
                          <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "#E65C00" }}>Feedback pending</Typography>
                          <Chip label="PENDING" size="small" sx={{ backgroundColor: "#FFD58C", color: "#7A3B00", fontWeight: 700, fontSize: "0.7rem" }} />
                        </Box>
                      )}

                      {/* Footer */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, pt: 1.5, borderTop: "1px solid #f0f0f0" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: "#999" }} />
                          <Typography sx={{ fontSize: "0.8rem", color: "#666" }}>Submitted On: {submittedDate}</Typography>
                        </Box>
                        <Button
                          onClick={() => openFeedback(sub)}
                          variant="contained"
                          size="small"
                          sx={{
                            borderRadius: 2,
                            backgroundColor: hasFeedback ? pal.chip : pal.btnBg,
                            color: hasFeedback ? pal.chipText : "#fff",
                            fontWeight: 600, fontSize: "0.8rem",
                            textTransform: "none", px: 2, boxShadow: "none",
                            "&:hover": { backgroundColor: hasFeedback ? pal.divider : pal.btnHover, boxShadow: "none" },
                          }}
                        >
                          {hasFeedback ? "Edit Feedback" : "Give Feedback"}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        ))
      )}

      {/* Feedback dialog */}
      {feedbackModal && (
        <Dialog
          open={!!feedbackModal}
          onClose={closeFeedback}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: { borderRadius: 3, display: "flex", flexDirection: "column" },
          }}
        >
          {/* ── Gradient header ── */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #3901ff 0%, #6a3de8 100%)",
              px: 2.5, pt: 2, pb: 1.75,
              display: "flex", justifyContent: "space-between", alignItems: "flex-start",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#fff" }}>ZM Feedback</Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.85)", mt: 0.2 }}>
                {feedbackModal.submission.user_name}
                {" · "}
                {MONTH_NAMES[feedbackModal.submission.month - 1]} {feedbackModal.submission.year}
              </Typography>
            </Box>
            <IconButton size="small" onClick={closeFeedback} sx={{ color: "rgba(255,255,255,0.8)", mt: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* ── Scrollable body ── */}
          <DialogContent sx={{ overflowY: "auto", px: 2.5, pt: 2, pb: 0 }}>
            {/* File pill */}
            <Box
              sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                backgroundColor: "#f5f5f5", borderRadius: 2, px: 1.75, py: 1, mb: 2,
              }}
            >
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: "#333", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mr: 1 }}>
                {feedbackModal.submission.original_file_name}
              </Typography>
              {feedbackModal.submission.file_path && (
                <Link href={feedbackModal.submission.file_path} target="_blank" rel="noopener noreferrer"
                  sx={{ color: "#3901ff", fontWeight: 600, fontSize: "0.8rem", flexShrink: 0 }}
                  underline="hover"
                >
                  Download ↗
                </Link>
              )}
            </Box>

            <RatingInput
              rating={rating} remarks={remarks}
              onRatingChange={setRating} onRemarksChange={setRemarks}
              disabled={submitFeedbackLoading} error={ratingError}
            />
          </DialogContent>

          {/* ── Actions ── */}
          <DialogActions sx={{ px: 2.5, pb: 2, pt: 1.5, gap: 1, borderTop: "1px solid #f0f0f0", flexShrink: 0, flexDirection: "column" }}>
            <CustomButton onClick={handleSubmitFeedback} disabled={submitFeedbackLoading} loading={submitFeedbackLoading} fullWidth>
              {submitFeedbackLoading ? "Submitting…" : "Submit Feedback"}
            </CustomButton>
            <CustomButton variant="outline" onClick={closeFeedback} disabled={submitFeedbackLoading} fullWidth>Cancel</CustomButton>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default NSMReviewList;
